import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { patientHistoryService } from '../services/patientHistoryService'
import { ConditionFormData, PatientCondition } from '../types/patientHistory'
import { terminologyService, TerminologySearchResult } from '../services/terminologyService'
import './ConditionForm.css'

interface ConditionFormProps {
  patientId: string
  initialData?: PatientCondition
  onSuccess: () => void
  onCancel: () => void
}

export function ConditionForm({ patientId, initialData, onSuccess, onCancel }: ConditionFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [conditionSearch, setConditionSearch] = useState('')
  const [conditionResults, setConditionResults] = useState<TerminologySearchResult[]>([])
  const [isSearchingConditions, setIsSearchingConditions] = useState(false)
  const [showConditionResults, setShowConditionResults] = useState(false)
  const conditionInputRef = useRef<HTMLInputElement>(null)
  const conditionResultsRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ConditionFormData>({
    defaultValues: initialData
      ? {
          condition: initialData.condition,
          diagnosisDate: initialData.diagnosisDate || '',
          status: initialData.status,
          notes: initialData.notes || '',
          severity: initialData.severity,
          onsetDate: initialData.onsetDate || '',
          resolvedDate: initialData.resolvedDate || '',
          icd10Code: initialData.icd10Code || '',
        }
      : {
          status: 'active',
        },
  })

  const status = watch('status')

  // Sync conditionSearch with conditionValue when form is initialized
  useEffect(() => {
    if (initialData?.condition) {
      setConditionSearch(initialData.condition)
    }
  }, [initialData?.condition])

  // Handle ICPC search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (!conditionSearch.trim()) {
      setConditionResults([])
      setShowConditionResults(false)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearchingConditions(true)
        const results = await terminologyService.search('ICPC2', conditionSearch, { limit: 20 })
        setConditionResults(results)
        setShowConditionResults(true)
      } catch (error) {
        console.error('Failed to search ICPC codes', error)
        setConditionResults([])
      } finally {
        setIsSearchingConditions(false)
      }
    }, 300) // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [conditionSearch])

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        conditionInputRef.current &&
        !conditionInputRef.current.contains(event.target as Node) &&
        conditionResultsRef.current &&
        !conditionResultsRef.current.contains(event.target as Node)
      ) {
        setShowConditionResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleConditionSearchChange = (value: string) => {
    setConditionSearch(value)
    setValue('condition', value, { shouldValidate: true })
  }

  const handleSelectCondition = (result: TerminologySearchResult) => {
    setValue('condition', result.display, { shouldValidate: true })
    setConditionSearch(result.display)
    setShowConditionResults(false)
    setConditionResults([])

    // If there's an ICD-10 mapping, populate the ICD-10 code field
    if (result.icd10Mappings && result.icd10Mappings.length > 0) {
      setValue('icd10Code', result.icd10Mappings[0])
    }
  }

  const onSubmit = async (data: ConditionFormData) => {
    setIsSubmitting(true)
    try {
      if (initialData) {
        await patientHistoryService.updateCondition(patientId, initialData.id, data)
      } else {
        await patientHistoryService.addCondition(patientId, data)
      }
      onSuccess()
    } catch (error) {
      console.error('Error saving condition:', error)
      alert('Failed to save condition')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="condition-form">
      <div className="form-group">
        <label htmlFor="condition">
          Condition <span className="required">*</span>
        </label>
        <div className="condition-search-wrapper" ref={conditionInputRef}>
          <input
            id="condition"
            type="text"
            value={conditionSearch}
            onChange={(e) => handleConditionSearchChange(e.target.value)}
            onFocus={() => {
              if (conditionResults.length > 0) {
                setShowConditionResults(true)
              }
            }}
            className={errors.condition ? 'error' : ''}
            placeholder="e.g., Type 2 Diabetes, Hypertension (search ICPC codes)"
            autoComplete="off"
          />
          {isSearchingConditions && (
            <div className="condition-search-loading">Searching ICPC codes...</div>
          )}
          {showConditionResults && conditionResults.length > 0 && (
            <div className="condition-search-results" ref={conditionResultsRef}>
              {conditionResults.map((result) => (
                <button
                  key={`${result.system}-${result.code}`}
                  type="button"
                  className="condition-search-result"
                  onClick={() => handleSelectCondition(result)}
                >
                  <span className="condition-search-code">
                    {result.system}:{result.code}
                  </span>
                  <span className="condition-search-display">{result.display}</span>
                  {result.icd10Mappings && result.icd10Mappings.length > 0 && (
                    <span className="condition-search-icd10">
                      ICD-10: {result.icd10Mappings[0]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        {errors.condition && (
          <span className="error-message">{errors.condition.message}</span>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="status">
            Status <span className="required">*</span>
          </label>
          <select
            id="status"
            {...register('status', { required: true })}
          >
            <option value="active">Active</option>
            <option value="chronic">Chronic</option>
            <option value="resolved">Resolved</option>
            <option value="history">History</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="severity">Severity</label>
          <select id="severity" {...register('severity')}>
            <option value="">Not specified</option>
            <option value="mild">Mild</option>
            <option value="moderate">Moderate</option>
            <option value="severe">Severe</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="icd10Code">ICD-10 Code</label>
          <input
            id="icd10Code"
            type="text"
            {...register('icd10Code')}
            placeholder="e.g., E11.9"
          />
        </div>

        <div className="form-group">
          <label htmlFor="diagnosisDate">Diagnosis Date</label>
          <input
            id="diagnosisDate"
            type="date"
            {...register('diagnosisDate')}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="onsetDate">Onset Date</label>
          <input
            id="onsetDate"
            type="date"
            {...register('onsetDate')}
          />
        </div>

        {status === 'resolved' && (
          <div className="form-group">
            <label htmlFor="resolvedDate">Resolved Date</label>
            <input
              id="resolvedDate"
              type="date"
              {...register('resolvedDate')}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={4}
          placeholder="Additional notes about this condition..."
        />
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Condition' : 'Add Condition'}
        </button>
      </div>
    </form>
  )
}

